import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Typography,
} from "@mui/material";
import PageHeader from "../../components/common/PageHeader";
import PageActions from "../../components/common/PageActions";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";

import { useProviderSearch } from "../../hooks/useMasterdataSearch";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import type { Provider } from "../../features/masterdata/types";
import { useExportProviderSummaryXlsxMutation, useLazyProviderSummaryQuery } from "../../features/analytics/analyticsApi";

import { getTodayString } from "../../utils/date";

type Scope = "completed" | "all";

const defaultFilters = {
  dateFrom: getTodayString(),
  dateTo: getTodayString(),
  provider: "",
  scope: "completed" as Scope,
};

export default function AnalyticsPage() {
  const [filters, setFilters] = usePersistedListFilters("analytics", defaultFilters);
  const { dateFrom, dateTo, provider, scope } = filters;

  const [isProviderOpen, setProviderOpen] = useState(false);
  const providerSearch = useProviderSearch(provider || null, { menuOpen: isProviderOpen });
  const providerOptions = useMemo(
    () => [{ id: "", name: "All providers" } as Provider, ...providerSearch.options],
    [providerSearch.options]
  );
  const isLoadingProviders = providerSearch.loading;

  // Lazy query: only run on button
  const [runSummary, { data, isFetching, error }] = useLazyProviderSummaryQuery();

  const rows = data?.results ?? [];

  const totals = useMemo(() => {
    return {
      transfers: data?.totals?.transfers ?? 0,
      sum_price_to_provider: data?.totals?.sum_price_to_provider ?? "0.00",
      sum_price_to_client: data?.totals?.sum_price_to_client ?? "0.00",
    };
  }, [data]);

  const onRun = async () => {
    if (!dateFrom || !dateTo) return;

    await runSummary({
      date_from: dateFrom,
      date_to: dateTo,
      provider: provider || "",
      include_all_statuses: scope === "all",
    });
  };

  const [exportXlsx, { isLoading: isExporting }] = useExportProviderSummaryXlsxMutation();

  const showNeedDates = !dateFrom || !dateTo;
  const showNoResults = !showNeedDates && !isFetching && !error && data && rows.length === 0;

  return (
    <Box>
      <PageHeader
        title="Analytics"
        subtitle="Provider billing-style summaries"
        actions={
          <PageActions
            onStartExport={() =>
              exportXlsx({
                date_from: dateFrom,
                date_to: dateTo,
                provider: provider || undefined,
                include_all_statuses: scope === "all",
              })
            }
            exportDisabled={!dateFrom || !dateTo || isExporting}
          />
        }
      />

      <FilterBar label="Report parameters" loading={isFetching || isLoadingProviders}>
        <TextField
          label="Date from"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setFilters({ dateFrom: e.target.value })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <TextField
          label="Date to"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setFilters({ dateTo: e.target.value })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <Box sx={{ minWidth: { xs: 0, sm: 200 }, width: { xs: "100%", sm: "auto" } }}>
          <AsyncAutocompleteWithQuickCreate<Provider>
            label="Provider"
            value={provider}
            onChange={(id) => setFilters({ provider: id ?? "" })}
            options={providerOptions}
            getOptionLabel={(p) => p.name}
            showCreate={false}
            loading={providerSearch.loading}
            onInputChange={(_, v) => providerSearch.setSearchTerm(v)}
            filterOptions={(opts) => opts}
            onOpen={() => setProviderOpen(true)}
            onClose={() => setProviderOpen(false)}
            placeholder="Type to search…"
          />
        </Box>
        <TextField
          label="Status scope"
          select
          value={scope}
          onChange={(e) => setFilters({ scope: e.target.value as Scope })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="completed">Completed only</MenuItem>
          <MenuItem value="all">All statuses</MenuItem>
        </TextField>
        <Button variant="contained" onClick={onRun} disabled={showNeedDates || isFetching}>
          {isFetching ? "Running…" : "Run"}
        </Button>
      </FilterBar>

      {error ? (
        <Typography sx={{ mt: 1 }} color="error">
          Failed to load analytics.
        </Typography>
      ) : null}

      {showNeedDates ? (
        <EmptyState title="Select a date range" description="Analytics require date_from and date_to." />
      ) : !data ? (
        <EmptyState title="Run analytics" description="Select filters and click Run to generate the report." />
      ) : showNoResults ? (
        <EmptyState title="No results" description="No transfers found for the selected range and filters." />
      ) : (
        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Provider Summary</Typography>
            <Typography variant="body2" color="text.secondary">
              Transfers: {totals.transfers} • Sum cost (provider): €{totals.sum_price_to_provider} • Sum charge (client): €
              {totals.sum_price_to_client}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ overflow: "auto" }}>
          <Table sx={{ minWidth: 400 }}>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell align="right">Transfers</TableCell>
                <TableCell align="right">Sum cost (provider)</TableCell>
                <TableCell align="right">Sum charge (client)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r: any) => (
                <TableRow key={r.provider_id} hover>
                  <TableCell>{r.provider_name}</TableCell>
                  <TableCell align="right">{r.transfers}</TableCell>
                  <TableCell align="right">€{r.sum_price_to_provider}</TableCell>
                  <TableCell align="right">€{r.sum_price_to_client}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
